import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
export function InvestmentCalculator() {
  const [initial, setInitial] = useState<number>(10000);
  const [monthly, setMonthly] = useState<number>(500);
  const [rate, setRate] = useState<number>(7);
  const [years, setYears] = useState<number>(20);
  const results = useMemo(() => {
    const p = initial || 0;
    const pmt = monthly || 0;
    const r = (rate || 0) / 100 / 12;
    const n = (years || 0) * 12;
    if (n === 0) {
      return {
        futureValue: p,
        totalInvested: p,
        totalGrowth: 0,
        chartData: []
      };
    }
    const chartData = [];
    let balance = p;
    let invested = p;
    for (let year = 0; year <= years; year++) {
      if (year === 0) {
        chartData.push({
          year,
          balance: Math.round(balance),
          invested: Math.round(invested)
        });
        continue;
      }
      for (let month = 1; month <= 12; month++) {
        balance = balance * (1 + r) + pmt;
        invested += pmt;
      }
      chartData.push({
        year,
        balance: Math.round(balance),
        invested: Math.round(invested)
      });
    }
    const futureValue = balance;
    const totalInvested = invested;
    const totalGrowth = futureValue - totalInvested;
    return {
      futureValue,
      totalInvested,
      totalGrowth,
      chartData
    };
  }, [initial, monthly, rate, years]);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(val);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-224">
      <div className="lg:col-span-1 space-y-6 bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-225">
        <h3 className="text-xl font-semibold text-white mb-4" data-id="element-226">
          Investment Details
        </h3>

        <div data-id="element-227">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-228">
            Initial Investment ($)
          </label>
          <input type="number" value={initial} onChange={e => setInitial(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" data-id="element-229" />
        </div>

        <div data-id="element-230">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-231">
            Monthly Contribution ($)
          </label>
          <input type="number" value={monthly} onChange={e => setMonthly(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" data-id="element-232" />
        </div>

        <div data-id="element-233">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-234">
            Expected Return (p.a. %)
          </label>
          <input type="number" step="0.1" value={rate} onChange={e => setRate(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" data-id="element-235" />
        </div>

        <div data-id="element-236">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-237">
            Time Horizon (Years)
          </label>
          <input type="number" value={years} onChange={e => setYears(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" data-id="element-238" />
        </div>
      </div>

      <div className="lg:col-span-2 space-y-6" data-id="element-239">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-240">
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-241">
            <p className="text-sm text-slate-400 mb-1" data-id="element-242">Future Value</p>
            <p className="text-3xl font-bold text-indigo-400" data-id="element-243">
              {formatCurrency(results.futureValue)}
            </p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-244">
            <p className="text-sm text-slate-400 mb-1" data-id="element-245">Total Invested</p>
            <p className="text-3xl font-bold text-white" data-id="element-246">
              {formatCurrency(results.totalInvested)}
            </p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-247">
            <p className="text-sm text-slate-400 mb-1" data-id="element-248">Total Growth</p>
            <p className="text-3xl font-bold text-green-400" data-id="element-249">
              {formatCurrency(results.totalGrowth)}
            </p>
          </div>
        </div>

        <div className="bg-white/5 p-6 rounded-2xl border border-white/10 h-[400px]" data-id="element-250">
          <h4 className="text-sm font-medium text-slate-400 mb-6" data-id="element-251">
            Wealth Projection
          </h4>
          <ResponsiveContainer width="100%" height="100%" data-id="element-252">
            <AreaChart data={results.chartData} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0
          }} data-id="element-253">
              <defs data-id="element-254">
                <linearGradient id="colorInvested" x1="0" y1="0" x2="0" y2="1" data-id="element-255">
                  <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3} data-id="element-256" />
                  <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} data-id="element-257" />
                </linearGradient>
                <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1" data-id="element-258">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.5} data-id="element-259" />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} data-id="element-260" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-261" />
              <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `Yr ${val}`} data-id="element-262" />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `$${val / 1000}k`} data-id="element-263" />
              <Tooltip contentStyle={{
              backgroundColor: '#0f172a',
              borderColor: '#1e293b',
              borderRadius: '8px',
              color: '#fff'
            }} formatter={(value: number) => formatCurrency(value)} labelFormatter={label => `Year ${label}`} data-id="element-264" />
              <Area type="monotone" dataKey="invested" name="Total Invested" stroke="#94a3b8" strokeWidth={2} fillOpacity={1} fill="url(#colorInvested)" stackId="1" data-id="element-265" />
              <Area type="monotone" dataKey="balance" name="Total Value" stroke="#6366f1" strokeWidth={2} fillOpacity={1} fill="url(#colorGrowth)" data-id="element-266" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}